import Vue from "vue";
import { Store } from "vuex";
import { Action, Module, Mutation, VuexModule, getModule } from "vuex-module-decorators";

import { processCoreModelResponse } from "@/store/-utils";
import { getProfilesStore } from "@/store/profiles";
import TrendingPostApi from "@/store/trending-posts/-api";

const emptyTrendingPostFeed: () => TrendingPostFeed = () => ({ ids: [], meta: null });

@Module({
  name: "trending-posts",
  stateFactory: true,
  namespaced: true,
})
export default class TrendingPostsStore extends VuexModule {
  trendingPosts: TrendingPostMap = {};
  feeds: TrendingPostFeeds = {};

  get getFeedHasMorePages() {
    return (feedName: string) => {
      const feed = this.trendingPostFeed(feedName);
      return (feed.ids.length === 0 && feed.meta === null) || Boolean(feed.meta?.next);
    };
  }

  get hasFeedBeenFetched() {
    return (feedName: string) => {
      const feed = this.trendingPostFeed(feedName);
      return feed && feed.meta !== null && feed.ids.length > 0;
    };
  }

  get trendingPostFeed() {
    return (feedName: string) => this.feeds[feedName] ?? emptyTrendingPostFeed();
  }

  @Action({ rawError: true })
  async fetchTrendingPostsByDisplayName({
    displayName,
    limit,
    overwrite = false,
  }: {
    displayName: string;
    limit?: number;
    overwrite?: boolean;
  }) {
    const profileStore = getProfilesStore(this.store);
    const profileId = profileStore.getIdByDisplayName(displayName);

    if (!profileId) {
      return;
    }

    if (!this.getFeedHasMorePages(displayName) && overwrite === false) {
      return;
    }

    const api = new TrendingPostApi(this.store.$axios);
    const data: Parameters<(typeof api)["getTrendingPostsByProfileId"]>[0] = { profileId, limit };
    const meta = this.feeds[displayName]?.meta || null;

    if (overwrite === false && meta?.next !== undefined) {
      data.next = meta.next;
    }

    const response = await api.getTrendingPostsByProfileId(data);
    const activities = response.data.activities;

    await processCoreModelResponse(this.store, response.data);

    // Posts are side-loaded, and come back out of order.
    // Activities are returned in the correct order, so we sort off of them.
    const ids = activities.map((activity) => activity.postId);

    this.setTrendingPostFeed({ feedName: displayName, ids, meta: response.data.meta, overwrite });
  }

  @Mutation
  setTrendingPosts(trendingPosts: TrendingPostMap) {
    Vue.set(this, "trendingPosts", Object.assign({}, this.trendingPosts, trendingPosts));
  }

  @Mutation
  setTrendingPostFeed({
    feedName,
    ids,
    meta,
    overwrite = false,
  }: {
    feedName: string;
    ids: string[];
    meta?: TrendingPostMeta | null;
    overwrite?: boolean;
  }) {
    if (this.feeds[feedName] === undefined) {
      Vue.set(this.feeds, feedName, { ids: [], meta: null });
    }

    const start = overwrite ? 0 : this.feeds[feedName].ids.length;
    const length = overwrite ? this.feeds[feedName].ids.length : 0;
    this.feeds[feedName].ids.splice(start, length, ...ids);

    Vue.set(this.feeds[feedName], "meta", meta ?? null);
  }
}

export const getTrendingPostsStore = (store: Store<unknown>) => {
  return getModule(TrendingPostsStore, store);
};
